// src/pages/SignupPage.tsx - User Signup Page
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Fade,
  Chip,
  Grid,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Person,
  Email,
  Lock,
  Phone,
  Work,
  Business,
  LinkedIn,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Visibility,
  VisibilityOff,
  TrendingUp,
  Analytics,
  Security,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { api } from '@services';
import axios, { AxiosError } from 'axios';
import { Button } from '@shared/components/ui/Button';
import designSystem from '@shared/utils/designSystem';
import {
  getGlassmorphismBackground,
  getPageBackgroundGradient,
  getBackgroundPattern,
  getBackButtonStyles,
  getTextFieldStyles
} from '../styles/authStyles';

// Type for API error responses
interface ApiErrorResponse {
  error?: string;
  message?: string;
  details?: Array<{ field: string; message: string }>;
}

// Type guard for checking if error has response
function isAxiosErrorWithResponse(error: unknown): error is AxiosError<ApiErrorResponse> {
  return axios.isAxiosError(error) && error.response !== undefined;
}

const expertiseAreas = [
  'Hotel Valuation',
  'Real Estate Investment',
  'Hotel Operations',
  'Asset Management',
  'Market Analysis',
  'Financial Modeling',
  'Due Diligence',
  'Hotel Development',
];

const steps = ['Personal Information', 'Professional Details'];

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    jobTitle: '',
    company: '',
    yearsOfExperience: '',
    certifications: [] as string[],
    areasOfExpertise: [] as string[],
    linkedinProfile: '',
    preferredContact: 'email',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: 'en',
    notificationSettings: {
      email: true,
      push: true
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [isPendingApproval, setIsPendingApproval] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only submit if we're on the final step
    if (currentStep !== steps.length - 1) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      interface SignupResponse {
        token?: string;
        user?: unknown;
        status?: string;
        message?: string;
      }
      const response = await api.post('/auth/signup', formData);
      const data = response.data as SignupResponse;

      // Check if signup is pending approval (202 status or pending status)
      if (response.status === 202 || data.status === 'pending') {
        // Show pending approval state
        setIsPendingApproval(true);
        setCurrentStep(steps.length); // Move beyond form steps to show success state
        setError(null);
        return;
      }

      // If user is immediately active (shouldn't happen for new users, but handle it)
      if (data?.token) {
        localStorage.setItem('token', data.token);

        if (data?.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        try {
          await register(formData);
          navigate('/dashboard');
        } catch {
          navigate('/dashboard');
        }
      } else {
        throw new Error('No token received from server');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '';

      if (isAxiosErrorWithResponse(error) && error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const validationErrors = error.response.data.details
          .map((detail) => `${detail.field}: ${detail.message}`)
          .join(', ');
        setError(`Validation failed: ${validationErrors}`);
      } else if (isAxiosErrorWithResponse(error) && error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (errorMessage === 'Network Error') {
        setError('Unable to connect to the server. Please check your network connection and make sure the backend is running.');
      } else if (axios.isAxiosError(error) && error.code === 'ECONNREFUSED') {
        setError('Backend server is not running. Please start the backend and try again.');
      } else {
        setError(errorMessage || 'Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isNextDisabled = () => {
    if (currentStep === 0) {
      return !formData.name || !formData.email || !formData.password;
    }
    return false;
  };

  const renderFormField = (
    label: string,
    children: React.ReactNode,
    required = false
  ) => (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: designSystem.spacing.xs / 8,
          color: theme.palette.text.primary,
          fontWeight: designSystem.typography.fontWeight.bold,
          fontSize: designSystem.typography.fontSize.sm,
          fontFamily: designSystem.typography.fontFamily.base,
        }}
      >
        {label} {required && <span style={{ color: theme.palette.error.main }}>*</span>}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: getPageBackgroundGradient(theme),
        position: 'relative',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: theme.palette.mode === 'light' ? 0.4 : 0.3,
          background: getBackgroundPattern(theme),
        }}
      />

      {/* Back to Landing Button */}
      <IconButton
        onClick={() => navigate('/')}
        sx={getBackButtonStyles(theme)}
      >
        <ArrowBack />
      </IconButton>

      <Grid container sx={{ minHeight: '100vh' }}>
        {/* Left Side - Branding */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm">
            <Box sx={{ textAlign: 'center', px: 4 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: `${designSystem.borderRadius.md}px`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: designSystem.spacing.lg / 8,
                  boxShadow: designSystem.shadows.primaryMd,
                }}
              >
                <Business sx={{ fontSize: 50, color: 'white' }} />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: designSystem.typography.fontWeight.bold,
                  mb: designSystem.spacing.md / 8,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  fontFamily: designSystem.typography.fontFamily.base,
                }}
              >
                Join Valuate
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: designSystem.spacing.xl / 8,
                  fontWeight: designSystem.typography.fontWeight.medium,
                  lineHeight: designSystem.typography.lineHeight.snug,
                  fontFamily: designSystem.typography.fontFamily.base,
                }}
              >
                Start Your Professional Hotel Valuation Journey
              </Typography>

              <Stack spacing={2.5}>
                {[
                  { icon: TrendingUp, text: 'Access Premium Market Data' },
                  { icon: Analytics, text: 'AI-Powered Valuation Tools' },
                  { icon: Security, text: 'Secure Professional Platform' },
                ].map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: `${designSystem.borderRadius.xs}px`,
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <feature.icon sx={{ fontSize: 18, color: theme.palette.primary.light }} />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: designSystem.typography.fontWeight.medium, color: theme.palette.text.secondary, fontFamily: designSystem.typography.fontFamily.base }}>
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Container>
        </Grid>

        {/* Right Side - Signup Form */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="md">
            <Fade appear={false} in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: `${designSystem.borderRadius.md}px`,
                  ...getGlassmorphismBackground(theme),
                  boxShadow: designSystem.shadows.xl,
                  overflow: 'hidden',
                  mx: { xs: 2, sm: 3 },
                  my: { xs: 4, md: 0 },
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                  {/* Mobile Logo */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 4 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: `${designSystem.borderRadius.sm}px`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: designSystem.shadows.primaryMd,
                      }}
                    >
                      <Business sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: designSystem.typography.fontWeight.bold,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: designSystem.typography.fontFamily.base,
                      }}
                    >
                      Join Valuate
                    </Typography>
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: designSystem.typography.fontWeight.bold,
                      mb: designSystem.spacing.xs / 8,
                      color: theme.palette.text.primary,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: { xs: '1.75rem', md: '2rem' },
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Create Your Account
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: designSystem.spacing.xl / 8,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: designSystem.typography.fontSize.lg,
                      fontWeight: designSystem.typography.fontWeight.medium,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Join thousands of hotel investment professionals
                  </Typography>

                  {/* Progress Stepper */}
                  <Box sx={{ mb: designSystem.spacing.xl / 8 }}>
                    <Stepper activeStep={currentStep} alternativeLabel>
                      {steps.map((label) => (
                        <Step key={label}>
                          <StepLabel
                            sx={{
                              '& .MuiStepLabel-label': {
                                fontSize: designSystem.typography.fontSize.sm,
                                fontWeight: designSystem.typography.fontWeight.semibold,
                                color: theme.palette.text.primary,
                                fontFamily: designSystem.typography.fontFamily.base,
                                '&.Mui-active': {
                                  color: theme.palette.primary.main,
                                },
                                '&.Mui-completed': {
                                  color: theme.palette.success.main,
                                },
                              },
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: designSystem.spacing.lg / 8,
                        borderRadius: `${designSystem.borderRadius.sm}px`,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        backgroundColor: alpha(theme.palette.error.main, 0.15),
                        color: theme.palette.mode === 'light'
                          ? theme.palette.error.dark
                          : theme.palette.error.light,
                        '& .MuiAlert-message': {
                          fontWeight: designSystem.typography.fontWeight.medium,
                          fontFamily: designSystem.typography.fontFamily.base,
                        },
                        '& .MuiAlert-icon': {
                          color: theme.palette.mode === 'light'
                            ? theme.palette.error.dark
                            : theme.palette.error.light,
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    {currentStep === 0 ? (
                      // Step 1: Personal Information
                      <Stack spacing={3}>
                        {renderFormField('Full Name', (
                          <TextField
                            fullWidth
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            InputProps={{
                              startAdornment: <Person sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={getTextFieldStyles(theme)}
                          />
                        ), true)}

                        {renderFormField('Email Address', (
                          <TextField
                            fullWidth
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email address"
                            InputProps={{
                              startAdornment: <Email sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={getTextFieldStyles(theme)}
                          />
                        ), true)}

                        {renderFormField('Password', (
                          <TextField
                            fullWidth
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            required
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Create a secure password"
                            InputProps={{
                              startAdornment: <Lock sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                              endAdornment: (
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  size="small"
                                  sx={{
                                    mr: 0.5,
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              ),
                            }}
                            sx={getTextFieldStyles(theme)}
                          />
                        ), true)}

                        {renderFormField('Phone Number', (
                          <TextField
                            fullWidth
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            placeholder="Enter your phone number"
                            InputProps={{
                              startAdornment: <Phone sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={getTextFieldStyles(theme)}
                          />
                        ))}
                      </Stack>
                    ) : (
                      // Step 2: Professional Information
                      <Stack spacing={3}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            {renderFormField('Job Title', (
                              <TextField
                                fullWidth
                                name="jobTitle"
                                value={formData.jobTitle}
                                onChange={handleChange}
                                placeholder="e.g., Investment Analyst"
                                InputProps={{
                                  startAdornment: <Work sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                                }}
                                sx={getTextFieldStyles(theme)}
                              />
                            ))}
                          </Grid>

                          <Grid item xs={12} md={6}>
                            {renderFormField('Company', (
                              <TextField
                                fullWidth
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder="Your company name"
                                InputProps={{
                                  startAdornment: <Business sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                                }}
                                sx={getTextFieldStyles(theme)}
                              />
                            ))}
                          </Grid>
                        </Grid>

                        {renderFormField('Years of Experience', (
                          <TextField
                            fullWidth
                            name="yearsOfExperience"
                            value={formData.yearsOfExperience}
                            onChange={handleChange}
                            placeholder="e.g., 5"
                            type="number"
                            sx={getTextFieldStyles(theme)}
                          />
                        ))}

                        {renderFormField('Areas of Expertise', (
                          <FormControl fullWidth>
                            <Select
                              multiple
                              name="areasOfExpertise"
                              value={formData.areasOfExpertise}
                              onChange={handleSelectChange}
                              displayEmpty
                              renderValue={(selected) => {
                                if (selected.length === 0) {
                                  return (
                                    <Typography sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                                      Select your areas of expertise
                                    </Typography>
                                  );
                                }
                                return (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map((value) => (
                                      <Chip
                                        key={value}
                                        label={value}
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          color: theme.palette.primary.main,
                                          fontWeight: 500,
                                        }}
                                      />
                                    ))}
                                  </Box>
                                );
                              }}
                              sx={{
                                borderRadius: `${designSystem.borderRadius.sm}px`,
                                backgroundColor: alpha(
                                  theme.palette.mode === 'light'
                                    ? theme.palette.background.default
                                    : theme.palette.grey[700],
                                  theme.palette.mode === 'light' ? 0.8 : 1
                                ),
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: alpha(theme.palette.primary.main, 0.5),
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: '2px',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: theme.palette.mode === 'light'
                                    ? '#ffffff'
                                    : theme.palette.grey[700],
                                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                                },
                                '& .MuiSelect-select': {
                                  py: designSystem.spacing.sm / 8 + 0.5,
                                  fontSize: designSystem.typography.fontSize.base,
                                  fontWeight: designSystem.typography.fontWeight.medium,
                                  fontFamily: designSystem.typography.fontFamily.base,
                                },
                                transition: `all ${designSystem.transitions.duration.normal}ms ${designSystem.transitions.easing.easeInOut}`,
                              }}
                            >
                              {expertiseAreas.map((area) => (
                                <MenuItem key={area} value={area}>
                                  {area}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ))}

                        {renderFormField('LinkedIn Profile', (
                          <TextField
                            fullWidth
                            name="linkedinProfile"
                            value={formData.linkedinProfile}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/your-profile"
                            InputProps={{
                              startAdornment: <LinkedIn sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />,
                            }}
                            sx={getTextFieldStyles(theme)}
                          />
                        ))}
                      </Stack>
                    )}

                    {/* Action Buttons */}
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: designSystem.spacing.xl / 8 }}>
                      <Button
                        type="button"
                        hierarchy="secondary"
                        onClick={currentStep === 0 ? () => navigate('/login') : handleBack}
                        sx={{
                          px: designSystem.spacing.xl / 8,
                          py: designSystem.spacing.sm / 8 + 0.5,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        {currentStep === 0 ? 'Back to Login' : 'Previous'}
                      </Button>

                      {currentStep < steps.length - 1 ? (
                        <Button
                          type="button"
                          hierarchy="primary"
                          onClick={handleNext}
                          disabled={isNextDisabled()}
                          endIcon={<ArrowForward />}
                          sx={{
                            px: designSystem.spacing.xl / 8,
                            py: designSystem.spacing.sm / 8 + 0.5,
                            fontFamily: designSystem.typography.fontFamily.base,
                          }}
                        >
                          Continue
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          hierarchy="primary"
                          disabled={loading}
                          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                          sx={{
                            px: designSystem.spacing.xl / 8,
                            py: designSystem.spacing.sm / 8 + 0.5,
                            fontFamily: designSystem.typography.fontFamily.base,
                            background: theme.palette.mode === 'light'
                              ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                              : theme.palette.success.main,
                            '&:hover': {
                              background: theme.palette.mode === 'light'
                                ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                                : theme.palette.success.dark,
                            },
                          }}
                        >
                          {loading ? 'Creating Account...' : 'Create Account'}
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  {/* Pending Approval Success State */}
                  {isPendingApproval && currentStep === steps.length && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: alpha(theme.palette.warning.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 48, color: theme.palette.warning.main }} />
                      </Box>

                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          color: theme.palette.text.primary,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        Account Pending Approval
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.secondary,
                          mb: 4,
                          maxWidth: 500,
                          mx: 'auto',
                          lineHeight: 1.6,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        Thank you for signing up! Your account has been submitted for review.
                        An administrator will review your application and you&apos;ll receive an email
                        notification once your account is approved.
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.disabled,
                          mb: 3,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        This typically takes 1-2 business days.
                      </Typography>

                      <Button
                        hierarchy="primary"
                        onClick={() => navigate('/')}
                        sx={{
                          px: 4,
                          py: 1.5,
                          fontWeight: 600,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        Return to Home
                      </Button>
                    </Box>
                  )}

                  {/* Already have account link */}
                  {!isPendingApproval && (
                    <Box
                      sx={{
                        mt: designSystem.spacing.xl / 8,
                        p: designSystem.spacing.lg / 8,
                        textAlign: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: `${designSystem.borderRadius.sm}px`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: designSystem.typography.fontWeight.medium, color: theme.palette.text.secondary, fontFamily: designSystem.typography.fontFamily.base }}>
                        Already have an account?{' '}
                        <Button
                          hierarchy="tertiary"
                          onClick={() => navigate('/login')}
                          sx={{
                            fontWeight: designSystem.typography.fontWeight.semibold,
                            color: theme.palette.primary.main,
                            fontFamily: designSystem.typography.fontFamily.base,
                            padding: 0,
                            minWidth: 'auto',
                            height: 'auto',
                          }}
                        >
                          Sign In
                        </Button>
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Fade>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SignupPage;
